import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useT } from '../lib/i18n.js';
import { useSession } from '../stores/session.js';
import { errorMessage } from '../lib/properties.js';
import { AddressAutocomplete } from './AddressAutocomplete.js';
import type { DealAddress } from '../lib/wizard-model.js';
import { listMembers, type TenantMember } from '../lib/members-api.js';
import {
  createOffice,
  listOffices,
  setOfficeArchived,
  setOfficeMembers,
  updateOffice,
  type OfficeDto,
  type OfficeInput,
} from '../lib/offices-api.js';
import { getTenantSettings, setBrokerReadScope } from '../lib/assistant-settings-api.js';

const OFFICES_QUERY_KEY = ['nastaveni', 'offices'] as const;
const OFFICE_MEMBERS_QUERY_KEY = ['nastaveni', 'offices', 'members'] as const;
const OFFICE_SCOPE_QUERY_KEY = ['nastaveni', 'offices', 'scope'] as const;

/**
 * Krok POB1 bod 2 — Nastavení → Kancelář → „Pobočky": seznam, detail,
 * přiřazení makléřů (vícenásobné) a přepínač rozsahu čtení makléře.
 *
 * JE TO KARTA NA ZÁLOŽCE „KANCELÁŘ", NE ŠESTÁ ZÁLOŽKA: `nastaveni.spec.tsx`
 * je chráněné měřidlo na PĚT záložek (nález N4 o sazbě na 390 px) — stejný
 * důvod, jaký má u sebe zapsaný „Tarif" (NAP11) a „Upozornění" (NAP6).
 *
 * Pobočka se NEMAZE, archivuje se; server archivaci odmítne, dokud na pobočce
 * visí neuzavřené zakázky (409 `office/has_open_cases`), a jeho `detail` se
 * zobrazí jako hláška — rozhodnutí je na serveru, tady se jen ukazuje.
 */
export function OfficesCard(): React.JSX.Element {
  const t = useT();
  const user = useSession((s) => s.user);
  const queryClient = useQueryClient();
  const isOwnerOrAdmin = (user?.roles ?? []).some((r) => r === 'owner' || r === 'admin');

  const [showArchived, setShowArchived] = React.useState(false);
  const offices = useQuery({
    queryKey: [...OFFICES_QUERY_KEY, showArchived] as const,
    queryFn: () => listOffices(showArchived),
  });
  const members = useQuery({ queryKey: OFFICE_MEMBERS_QUERY_KEY, queryFn: listMembers });
  const settings = useQuery({ queryKey: OFFICE_SCOPE_QUERY_KEY, queryFn: getTenantSettings });

  /** `null` = nic se needituje, `'new'` = formulář nové pobočky. */
  const [editing, setEditing] = React.useState<string | null>(null);
  const [note, setNote] = React.useState<{ readonly ok: boolean; readonly text: string } | null>(null);
  const [busy, setBusy] = React.useState(false);

  async function reload(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: OFFICES_QUERY_KEY });
  }

  async function onArchive(office: OfficeDto, archived: boolean): Promise<void> {
    setBusy(true);
    setNote(null);
    try {
      await setOfficeArchived(office.id, archived);
      await reload();
      setNote({ ok: true, text: t('webAdmin.settings.offices.saved') });
    } catch (e) {
      setNote({ ok: false, text: t('webAdmin.settings.offices.error', { error: errorMessage(e) }) });
    } finally {
      setBusy(false);
    }
  }

  async function onScopeChange(next: 'tenant' | 'office'): Promise<void> {
    setBusy(true);
    setNote(null);
    try {
      await setBrokerReadScope(next);
      await queryClient.invalidateQueries({ queryKey: OFFICE_SCOPE_QUERY_KEY });
      setNote({ ok: true, text: t('webAdmin.settings.offices.saved') });
    } catch (e) {
      setNote({ ok: false, text: t('webAdmin.settings.offices.error', { error: errorMessage(e) }) });
    } finally {
      setBusy(false);
    }
  }

  const items = offices.data ?? [];

  return (
    <div className="rh-card" style={{ maxWidth: 640, padding: 16, marginBottom: 16 }} data-testid="nastaveni-offices-card">
      <h2 className="mb-1 text-base font-semibold">{t('webAdmin.settings.offices.title')}</h2>
      <p className="mb-3 text-sm text-gray-600">{t('webAdmin.settings.offices.perex')}</p>

      <ul className="mb-3 list-none p-0" data-testid="offices-list">
        {items.map((office) => (
          <li
            key={office.id}
            className="mb-2 rounded border p-2"
            data-testid={`office-row-${office.id}`}
          >
            {/* Mobil 390: jméno a tlačítka se zalomí pod sebe (flex-wrap). */}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm font-medium" data-testid={`office-name-${office.id}`}>
                {office.name}
                {office.archivedAt !== null ? (
                  <span className="ml-2 text-xs text-gray-600" data-testid={`office-archived-${office.id}`}>
                    {t('webAdmin.settings.offices.archivedBadge')}
                  </span>
                ) : null}
              </span>
              <span className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="rh-a2-link text-xs"
                  data-testid={`office-edit-${office.id}`}
                  onClick={() => setEditing(editing === office.id ? null : office.id)}
                >
                  {t('webAdmin.settings.offices.editButton')}
                </button>
                {isOwnerOrAdmin ? (
                  <button
                    type="button"
                    className="rh-a2-link text-xs"
                    disabled={busy}
                    data-testid={`office-archive-${office.id}`}
                    onClick={() => void onArchive(office, office.archivedAt === null)}
                  >
                    {office.archivedAt === null
                      ? t('webAdmin.settings.offices.archiveButton')
                      : t('webAdmin.settings.offices.restoreButton')}
                  </button>
                ) : null}
              </span>
            </div>
            <p className="mt-1 text-xs text-gray-600" data-testid={`office-summary-${office.id}`}>
              {office.addressText ?? t('webAdmin.settings.offices.noAddress')}
              {' · '}
              {t('webAdmin.settings.offices.memberCount', { count: String(office.memberUserIds.length) })}
              {' · '}
              {t('webAdmin.settings.offices.openCases', { count: String(office.openCaseCount) })}
            </p>
            {editing === office.id ? (
              <OfficeForm
                key={office.id}
                office={office}
                members={members.data ?? []}
                canEdit={isOwnerOrAdmin}
                onDone={async (text) => {
                  setNote(text);
                  await reload();
                  if (text.ok) setEditing(null);
                }}
              />
            ) : null}
          </li>
        ))}
      </ul>

      {items.length === 0 && !offices.isLoading ? (
        <p className="mb-3 text-sm text-gray-600" data-testid="offices-empty">
          {t('webAdmin.settings.offices.empty')}
        </p>
      ) : null}

      <div className="mb-3 flex flex-wrap items-center gap-3">
        {isOwnerOrAdmin ? (
          <button
            type="button"
            className="rh-a2-btn rh-a2-btn--primary"
            data-testid="offices-add"
            onClick={() => setEditing(editing === 'new' ? null : 'new')}
          >
            {t('webAdmin.settings.offices.addButton')}
          </button>
        ) : null}
        <label className="text-xs text-gray-600">
          <input
            type="checkbox"
            className="mr-1"
            data-testid="offices-show-archived"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          {t('webAdmin.settings.offices.showArchived')}
        </label>
      </div>

      {editing === 'new' ? (
        <OfficeForm
          key="new"
          office={null}
          members={members.data ?? []}
          canEdit={isOwnerOrAdmin}
          onDone={async (text) => {
            setNote(text);
            await reload();
            if (text.ok) setEditing(null);
          }}
        />
      ) : null}

      {/*
        Rozsah čtení makléře. Patří k pobočkám, protože bez pobočky nemá co
        zúžit — makléř bez přiřazené pobočky při `office` neuvidí NIC
        (`brokerOfficeListScope` → `none`), a právě proto je hláška u volby.
      */}
      <fieldset className="border-t pt-3" data-testid="offices-scope">
        <legend className="text-sm font-medium">{t('webAdmin.settings.offices.scopeLabel')}</legend>
        {(['tenant', 'office'] as const).map((value) => (
          <label key={value} className="mt-1 block text-sm">
            <input
              type="radio"
              name="broker-read-scope"
              className="mr-2"
              data-testid={`offices-scope-${value}`}
              disabled={!isOwnerOrAdmin || busy || settings.data === undefined}
              checked={(settings.data?.brokerReadScope ?? 'tenant') === value}
              onChange={() => void onScopeChange(value)}
            />
            {value === 'tenant'
              ? t('webAdmin.settings.offices.scopeTenant')
              : t('webAdmin.settings.offices.scopeOffice')}
          </label>
        ))}
        <p className="mt-1 text-xs text-gray-600">{t('webAdmin.settings.offices.scopeHint')}</p>
      </fieldset>

      {note !== null ? (
        <p
          className={`mt-2 text-sm ${note.ok ? 'text-green-700' : 'text-red-700'}`}
          role={note.ok ? 'status' : 'alert'}
          data-testid="offices-note"
        >
          {note.text}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Formulář pobočky. Jeden `Uložit` zapíše OBOJÍ — údaje pobočky i přiřazení
 * makléřů: pro vedoucího kanceláře je to jedna věc k vyřízení, a dvě tlačítka
 * by znamenala stav „uložil jsem jen půlku".
 *
 * Stav se plní z props přes `key` (nová instance při změně pobočky), ne
 * v `useEffect` — efekt po připojení přepíše rozepsaný text (poučení
 * `reos-web-admin-mount-effect-race`).
 */
function OfficeForm({
  office,
  members,
  canEdit,
  onDone,
}: {
  readonly office: OfficeDto | null;
  readonly members: readonly TenantMember[];
  readonly canEdit: boolean;
  readonly onDone: (note: { readonly ok: boolean; readonly text: string }) => Promise<void>;
}): React.JSX.Element {
  const t = useT();
  const prefix = office === null ? 'office-new' : `office-edit-${office.id}`;
  const [name, setName] = React.useState(office?.name ?? '');
  const [address, setAddress] = React.useState<DealAddress>({
    query: office?.addressText ?? '',
    lat: office?.addressLat === null || office?.addressLat === undefined ? null : Number(office.addressLat),
    lon: office?.addressLon === null || office?.addressLon === undefined ? null : Number(office.addressLon),
    source: 'manual',
  });
  const [email, setEmail] = React.useState(office?.email ?? '');
  const [phone, setPhone] = React.useState(office?.phone ?? '');
  const [ico, setIco] = React.useState(office?.ico ?? '');
  const [establishmentId, setEstablishmentId] = React.useState(office?.establishmentId ?? '');
  const [invoiceSeries, setInvoiceSeries] = React.useState(office?.invoiceSeries ?? '');
  const [memberIds, setMemberIds] = React.useState<readonly string[]>(office?.memberUserIds ?? []);
  const [busy, setBusy] = React.useState(false);

  function toggleMember(userId: string): void {
    setMemberIds((current) =>
      current.includes(userId) ? current.filter((id) => id !== userId) : [...current, userId],
    );
  }

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    try {
      const input: OfficeInput = {
        name: name.trim(),
        addressText: address.query.trim() === '' ? null : address.query.trim(),
        addressLat: address.lat === null ? null : String(address.lat),
        addressLon: address.lon === null ? null : String(address.lon),
        email: email.trim() === '' ? null : email.trim(),
        phone: phone.trim() === '' ? null : phone.trim(),
        ico: ico.trim() === '' ? null : ico.trim(),
        establishmentId: establishmentId.trim() === '' ? null : establishmentId.trim(),
        invoiceSeries: invoiceSeries.trim() === '' ? null : invoiceSeries.trim(),
      };
      const saved =
        office === null
          ? await createOffice({ ...input, name: name.trim() })
          : await updateOffice(office.id, input);
      await setOfficeMembers(saved.id, memberIds);
      await onDone({ ok: true, text: t('webAdmin.settings.offices.saved') });
    } catch (err) {
      await onDone({ ok: false, text: t('webAdmin.settings.offices.error', { error: errorMessage(err) }) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="mt-2 border-t pt-2" onSubmit={(e) => void onSubmit(e)} data-testid={`${prefix}-form`}>
      <label className="mb-1 block text-sm font-medium" htmlFor={`${prefix}-name`}>
        {t('webAdmin.settings.offices.nameLabel')}
      </label>
      <input
        id={`${prefix}-name`}
        data-testid={`${prefix}-name`}
        className="mb-2 block w-full rounded border px-2 py-1 text-sm"
        value={name}
        disabled={!canEdit}
        onChange={(e) => setName(e.target.value)}
      />

      {/* Adresa z našeptávače (RÚIAN) — tatáž komponenta jako průvodce zakázkou. */}
      <div className="mb-2" data-testid={`${prefix}-address`}>
        <AddressAutocomplete value={address} onChange={setAddress} testIdPrefix={`${prefix}-address-suggest`} />
      </div>

      <div className="flex flex-wrap gap-2">
        <TextField id={`${prefix}-email`} label={t('webAdmin.settings.offices.emailLabel')} value={email} onChange={setEmail} disabled={!canEdit} />
        <TextField id={`${prefix}-phone`} label={t('webAdmin.settings.offices.phoneLabel')} value={phone} onChange={setPhone} disabled={!canEdit} />
        <TextField id={`${prefix}-ico`} label={t('webAdmin.settings.offices.icoLabel')} value={ico} onChange={setIco} disabled={!canEdit} />
        <TextField
          id={`${prefix}-establishment`}
          label={t('webAdmin.settings.offices.establishmentLabel')}
          value={establishmentId}
          onChange={setEstablishmentId}
          disabled={!canEdit}
        />
        <TextField
          id={`${prefix}-invoice-series`}
          label={t('webAdmin.settings.offices.invoiceSeriesLabel')}
          value={invoiceSeries}
          onChange={setInvoiceSeries}
          disabled={!canEdit}
        />
      </div>

      <p className="mb-1 mt-2 text-sm font-medium">{t('webAdmin.settings.offices.membersLabel')}</p>
      {members.length === 0 ? (
        <p className="text-xs text-gray-600" data-testid={`${prefix}-members-empty`}>
          {t('webAdmin.settings.offices.membersEmpty')}
        </p>
      ) : (
        <ul className="mb-2 list-none p-0" data-testid={`${prefix}-members`}>
          {members.map((m) => (
            <li key={m.userId}>
              <label className="text-sm">
                <input
                  type="checkbox"
                  className="mr-2"
                  data-testid={`${prefix}-member-${m.userId}`}
                  disabled={!canEdit}
                  checked={memberIds.includes(m.userId)}
                  onChange={() => toggleMember(m.userId)}
                />
                {m.name === '' ? m.email : m.name}
              </label>
            </li>
          ))}
        </ul>
      )}

      <button
        type="submit"
        className="rh-a2-btn rh-a2-btn--primary"
        disabled={!canEdit || busy || name.trim() === ''}
        data-testid={`${prefix}-save`}
      >
        {t('webAdmin.settings.offices.saveButton')}
      </button>
    </form>
  );
}

function TextField({
  id,
  label,
  value,
  onChange,
  disabled,
}: {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly disabled: boolean;
}): React.JSX.Element {
  return (
    <span className="min-w-0 flex-1" style={{ minWidth: 160 }}>
      <label className="mb-1 block text-xs font-medium" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        data-testid={id}
        className="mb-1 block w-full rounded border px-2 py-1 text-sm"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
    </span>
  );
}
